import { cssVar } from '@helpers/cssVar';
import { withMotion } from '@hoc/withMotion';
import { ReactComponent as SvgArrowLeft } from '@img/icons/arrow-left.svg';
import { ReactComponent as SvgArrowRight } from '@img/icons/arrow-right.svg';
import { ReactComponent as SvgPause } from '@img/icons/pause.svg';
import { ReactComponent as SvgTriangleLeft } from '@img/icons/triangle-left.svg';
import { ReactComponent as SvgTriangleRight } from '@img/icons/triangle-right.svg';
import { useAnimation } from 'framer-motion';
import * as React from 'react';
import { useInterval } from 'react-use';
import S from './CarouselControls.styles';
import { useCarousel } from './CarouselProvider';
const CarouselControls = ({ controls = false, theme = 'light', icons = 'default', }) => {
    const progressRadius = 18;
    const circumference = Math.PI * progressRadius * 2;
    const { actions, state } = useCarousel();
    const { next, playPause, prev } = actions;
    const { isPaused, autoplay = 0, disabled = false, loop = false, max, value, onChange } = state;
    const animationControls = useAnimation();
    useInterval(() => {
        next();
    }, isPaused ? null : autoplay);
    React.useEffect(() => {
        if (value === max - 1 && !loop) {
            return;
        }
        if (isPaused) {
            animationControls.start({
                strokeDashoffset: circumference,
                transition: { duration: 0.3, ease: 'easeOut', type: 'tween' },
            });
            return;
        }
        animationControls.set({
            strokeDashoffset: circumference,
        });
        animationControls.start({
            strokeDashoffset: 0,
            transition: { duration: autoplay / 1000, ease: 'linear', type: 'tween' },
        });
    }, [isPaused, value]);
    return (React.createElement(S.Container, { themeOption: theme === 'shard-lights' ? 'dark' : theme },
        new Array(max).fill(0).map((_, index) => (React.createElement(S.SlideButton, { key: index + 1, disabled: value === index, onClick: (event) => {
                event.preventDefault();
                if (!disabled) {
                    playPause(true);
                    onChange(index);
                }
            } },
            React.createElement(S.SrOnly, null, value === index ? 'Active' : index + 1)))),
        autoplay > 0 && (React.createElement(S.ControlProgressButton, { onClick: (event) => {
                event.preventDefault();
                if (!isPaused) {
                    return playPause(true);
                }
                if (!disabled) {
                    playPause(false);
                    next();
                }
            } },
            React.createElement(S.SrOnly, null, isPaused ? 'Play' : 'Pause'),
            React.createElement(S.ControlIcon, { as: isPaused ? SvgTriangleRight : SvgPause }),
            React.createElement(S.ControlProgress, { viewBox: "0 0 40 40" },
                React.createElement("circle", { cx: 20, cy: 20, fill: "none", r: progressRadius, strokeWidth: 2, style: { stroke: cssVar('fgColor') } }),
                theme === 'shard-lights' && (React.createElement("defs", null,
                    React.createElement("linearGradient", { id: "gradientBg" },
                        React.createElement("stop", { stopColor: "#ed4141" }),
                        React.createElement("stop", { offset: ".071", stopColor: "#ef523d" }),
                        React.createElement("stop", { offset: ".143", stopColor: "#f0623a" }),
                        React.createElement("stop", { offset: ".214", stopColor: "#f17138" }),
                        React.createElement("stop", { offset: ".286", stopColor: "#f07e37" }),
                        React.createElement("stop", { offset: ".357", stopColor: "#f08b39" }),
                        React.createElement("stop", { offset: ".429", stopColor: "#ef983c" }),
                        React.createElement("stop", { offset: ".5", stopColor: "#eda441" }),
                        React.createElement("stop", { offset: ".571", stopColor: "#d8a936" }),
                        React.createElement("stop", { offset: ".643", stopColor: "#c2ae30" }),
                        React.createElement("stop", { offset: ".714", stopColor: "#aab231" }),
                        React.createElement("stop", { offset: ".786", stopColor: "#90b439" }),
                        React.createElement("stop", { offset: ".857", stopColor: "#73b645" }),
                        React.createElement("stop", { offset: ".929", stopColor: "#50b754" }),
                        React.createElement("stop", { offset: "1", stopColor: "#08b766" })))),
                React.createElement(S.ControlProgressCircle, { animate: animationControls, cx: 20, cy: 20, fill: "none", r: progressRadius, strokeWidth: 2, 
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    style: {
                        ['--circumference']: circumference,
                        stroke: theme === 'shard-lights' ? 'url(#gradientBg)' : cssVar('accentColor'),
                    } })))),
        controls && (React.createElement(React.Fragment, null,
            React.createElement(S.ControlButton, { disabled: !loop && value === 0, onClick: (event) => {
                    event.preventDefault();
                    if (!disabled) {
                        playPause(true);
                        prev();
                    }
                }, "data-icon": icons },
                React.createElement(S.SrOnly, null, "Previous"),
                React.createElement(S.ControlIcon, { as: icons == 'arrows' ? SvgArrowLeft : SvgTriangleLeft })),
            React.createElement(S.ControlButton, { disabled: !loop && value === max - 1, onClick: (event) => {
                    event.preventDefault();
                    if (!disabled) {
                        playPause(true);
                        next();
                    }
                }, "data-icon": icons },
                React.createElement(S.SrOnly, null, "Next"),
                React.createElement(S.ControlIcon, { as: icons == 'arrows' ? SvgArrowRight : SvgTriangleRight }))))));
};
export default withMotion(CarouselControls);
